import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

// This Powder Blue scene was rejected because the rendered CP pad structure is
// incorrect. Keep the Shopify media recoverable, but never render it in the CP
// storefront gallery. This guard is independent of whichever media-gallery
// class the theme registers first.
if (location.pathname === '/products/nuzzlenest-orthopedic-dog-crate-pad') {
  const rejectedCpPowderBlueStyle = document.createElement('style');
  rejectedCpPowderBlueStyle.dataset.nnRejectedCpPowderBlue = '';
  rejectedCpPowderBlueStyle.textContent = `
    media-gallery slideshow-slide:has(img[src*="05-powder-blue"], img[srcset*="05-powder-blue"], source[srcset*="05-powder-blue"]),
    media-gallery .slideshow-controls__thumbnail:has(img[src*="05-powder-blue"], img[srcset*="05-powder-blue"], source[srcset*="05-powder-blue"]),
    media-gallery .media-gallery__grid > li:has(img[src*="05-powder-blue"], img[srcset*="05-powder-blue"], source[srcset*="05-powder-blue"]),
    media-gallery .dialog-thumbnails-list button:has(img[src*="05-powder-blue"], img[srcset*="05-powder-blue"], source[srcset*="05-powder-blue"]) {
      display: none !important;
    }
  `;
  document.head.append(rejectedCpPowderBlueStyle);
}

// Keep all four Luxury Donut (LD) diameter options on one horizontal row on
// desktop. The default equal-width picker reserves too much minimum width,
// which pushes 43 in onto a second row even when the product panel has room.
if (location.pathname === '/products/nuzzlenest-luxury-donut-dog-bed') {
  const ldHorizontalSizePickerStyle = document.createElement('style');
  ldHorizontalSizePickerStyle.dataset.nnLdHorizontalSizePicker = '';
  ldHorizontalSizePickerStyle.textContent = `
    .nn-compact-size-picker.variant-option--equal-width-buttons {
      --variant-min-width: 0px !important;
      grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
      gap: 10px !important;
    }
    .nn-compact-size-picker .variant-option__button-label {
      width: 100% !important;
      min-width: 0 !important;
      min-height: 56px !important;
      padding: 10px 6px !important;
    }
    .nn-compact-size-picker .variant-option__button-label__text {
      display: block !important;
      width: 100% !important;
      white-space: nowrap !important;
      text-wrap: nowrap !important;
      word-break: keep-all !important;
      overflow-wrap: normal !important;
      text-align: center !important;
      font-size: 14px !important;
      line-height: 1.2 !important;
    }
    @media (max-width: 749px) {
      .nn-compact-size-picker.variant-option--equal-width-buttons {
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      }
    }
  `;
  document.head.append(ldHorizontalSizePickerStyle);
}

// Preserve superseded Oxford size-guide media in Shopify, but show only the
// final five-card guide (v3) that retains the original design.
if (location.pathname === '/products/nuzzlenest-oxford-waterproof-crate-bed') {
  const bmHorizontalSizePickerStyle = document.createElement('style');
  bmHorizontalSizePickerStyle.dataset.nnBmHorizontalSizePicker = '';
  bmHorizontalSizePickerStyle.textContent = `
    .nn-compact-size-picker.variant-option--equal-width-buttons {
      --variant-min-width: 0px !important;
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 10px !important;
    }
    .nn-compact-size-picker .variant-option__button-label {
      width: 100% !important;
      min-width: 0 !important;
      min-height: 48px !important;
      padding: 10px 8px !important;
    }
    .nn-compact-size-picker .variant-option__button-label__text {
      display: block !important;
      width: 100% !important;
      white-space: nowrap !important;
      text-wrap: nowrap !important;
      word-break: keep-all !important;
      overflow-wrap: normal !important;
      text-align: center !important;
      font-size: 14px !important;
      line-height: 1.2 !important;
    }
  `;
  document.head.append(bmHorizontalSizePickerStyle);

  const obsoleteBmSizeGuideStyle = document.createElement('style');
  obsoleteBmSizeGuideStyle.dataset.nnObsoleteBmSizeGuide = '';
  obsoleteBmSizeGuideStyle.textContent = `
    media-gallery slideshow-slide:has(img[src*="size-guide-v1"], img[srcset*="size-guide-v1"], source[srcset*="size-guide-v1"]),
    media-gallery slideshow-slide:has(img[src*="size-guide-v2"], img[srcset*="size-guide-v2"], source[srcset*="size-guide-v2"]),
    media-gallery .slideshow-controls__thumbnail:has(img[src*="size-guide-v1"], img[srcset*="size-guide-v1"], source[srcset*="size-guide-v1"]),
    media-gallery .slideshow-controls__thumbnail:has(img[src*="size-guide-v2"], img[srcset*="size-guide-v2"], source[srcset*="size-guide-v2"]),
    media-gallery .media-gallery__grid > li:has(img[src*="size-guide-v1"], img[srcset*="size-guide-v1"], source[srcset*="size-guide-v1"]),
    media-gallery .media-gallery__grid > li:has(img[src*="size-guide-v2"], img[srcset*="size-guide-v2"], source[srcset*="size-guide-v2"]),
    media-gallery .dialog-thumbnails-list button:has(img[src*="size-guide-v1"], img[srcset*="size-guide-v1"], source[srcset*="size-guide-v1"]),
    media-gallery .dialog-thumbnails-list button:has(img[src*="size-guide-v2"], img[srcset*="size-guide-v2"], source[srcset*="size-guide-v2"]) {
      display: none !important;
    }
  `;
  document.head.append(obsoleteBmSizeGuideStyle);

  // Rejected product-only closeups and the malformed crate scene must stay
  // out of the storefront even if Shopify still serves legacy duplicate
  // uploads. In their place, expose visually checked pet lifestyle scenes.
  const bmReplacementSceneStyle = document.createElement('style');
  bmReplacementSceneStyle.dataset.nnBmReplacementScenes = '';
  bmReplacementSceneStyle.textContent = `
    media-gallery slideshow-slide:has(img[src*="06-gray-low-edge"], img[srcset*="06-gray-low-edge"], source[srcset*="06-gray-low-edge"]),
    media-gallery slideshow-slide:has(img[src*="07-gray-material"], img[srcset*="07-gray-material"], source[srcset*="07-gray-material"]),
    media-gallery slideshow-slide:has(img[src*="05-gray-crate-use"], img[srcset*="05-gray-crate-use"], source[srcset*="05-gray-crate-use"]),
    media-gallery .slideshow-controls__thumbnail:has(img[src*="06-gray-low-edge"], img[srcset*="06-gray-low-edge"], source[srcset*="06-gray-low-edge"]),
    media-gallery .slideshow-controls__thumbnail:has(img[src*="07-gray-material"], img[srcset*="07-gray-material"], source[srcset*="07-gray-material"]),
    media-gallery .slideshow-controls__thumbnail:has(img[src*="05-gray-crate-use"], img[srcset*="05-gray-crate-use"], source[srcset*="05-gray-crate-use"]) {
      display: none !important;
    }
    media-gallery slideshow-slide:has(img[src*="bm-oxford-gray-36-beagle-mudroom-v2"], img[srcset*="bm-oxford-gray-36-beagle-mudroom-v2"], source[srcset*="bm-oxford-gray-36-beagle-mudroom-v2"]),
    media-gallery slideshow-slide:has(img[src*="bm-oxford-gray-42-black-lab-living-room-v2"], img[srcset*="bm-oxford-gray-42-black-lab-living-room-v2"], source[srcset*="bm-oxford-gray-42-black-lab-living-room-v2"]),
    media-gallery slideshow-slide:has(img[src*="bm-oxford-gray-48-bernese-fireplace-v2"], img[srcset*="bm-oxford-gray-48-bernese-fireplace-v2"], source[srcset*="bm-oxford-gray-48-bernese-fireplace-v2"]),
    media-gallery .slideshow-controls__thumbnail:has(img[src*="bm-oxford-gray-36-beagle-mudroom-v2"], img[srcset*="bm-oxford-gray-36-beagle-mudroom-v2"], source[srcset*="bm-oxford-gray-36-beagle-mudroom-v2"]),
    media-gallery .slideshow-controls__thumbnail:has(img[src*="bm-oxford-gray-42-black-lab-living-room-v2"], img[srcset*="bm-oxford-gray-42-black-lab-living-room-v2"], source[srcset*="bm-oxford-gray-42-black-lab-living-room-v2"]),
    media-gallery .slideshow-controls__thumbnail:has(img[src*="bm-oxford-gray-48-bernese-fireplace-v2"], img[srcset*="bm-oxford-gray-48-bernese-fireplace-v2"], source[srcset*="bm-oxford-gray-48-bernese-fireplace-v2"]) {
      display: block !important;
    }
  `;
  document.head.append(bmReplacementSceneStyle);

  const ensureBmReplacementScenes = () => {
    const gallery = document.querySelector('media-gallery');
    if (!gallery) return;
    const replacementNeedles = [
      'bm-oxford-gray-36-beagle-mudroom-v2',
      'bm-oxford-gray-42-black-lab-living-room-v2',
      'bm-oxford-gray-48-bernese-fireplace-v2',
    ];
    for (const element of gallery.querySelectorAll('slideshow-slide, .slideshow-controls__thumbnail')) {
      const mediaSource = [...element.querySelectorAll('img, source')]
        .flatMap((media) => [media.currentSrc, media.src, media.srcset, media.getAttribute('src'), media.getAttribute('srcset')])
        .filter(Boolean)
        .join(' ');
      if (!replacementNeedles.some((needle) => mediaSource.includes(needle))) continue;
      element.hidden = false;
      element.removeAttribute('data-nn-color-hidden');
      element.style.removeProperty('display');
    }
  };
  const scheduleBmReplacementScenes = () => {
    requestAnimationFrame(ensureBmReplacementScenes);
    setTimeout(ensureBmReplacementScenes, 200);
    setTimeout(ensureBmReplacementScenes, 900);
    setTimeout(ensureBmReplacementScenes, 2200);
  };
  scheduleBmReplacementScenes();
  new MutationObserver(() => requestAnimationFrame(ensureBmReplacementScenes))
    .observe(document.documentElement, { childList: true, subtree: true });
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    target?.addEventListener('change', this.#handleColorChange, { signal });
    this.addEventListener('click', this.#handleVisibleThumbnailClick, { capture: true, signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.slideshow?.addEventListener('slideshow:select', this.#handleSlideshowSelect, { signal });

    this.#filterPsm04Media();
    setTimeout(() => {
    const index = Number(this.slideshow?.current ?? 0);
    if (index > 0) this.#handleSlideshowSelect({ detail: { index } });
  }, 250);
  }

  #controller = new AbortController();

  #handleColorChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.type !== 'radio' || !event.target.name.startsWith('Color-')) return;
    delete this.dataset.nnMobileThumbOrder;
    this.querySelectorAll('.slideshow-controls__thumbnail').forEach((thumbnail) => thumbnail.style.removeProperty('order'));
    this.#filterPsm04Media();
  };

  #handleSlideshowSelect = (event) => {
    const selectedIndex = Number(event.detail?.index ?? 0);
    const visibleThumbnails = [...this.querySelectorAll('.slideshow-controls__thumbnail')]
      .filter((thumbnail) => !thumbnail.hasAttribute('data-nn-color-hidden'));

    visibleThumbnails.forEach((thumbnail, index) => {
      thumbnail.setAttribute('aria-selected', String(index === selectedIndex));
    });
    if (location.pathname === '/products/nuzzlenest-luxury-donut-dog-bed' &&
      window.matchMedia('(max-width: 749px)').matches &&
      !this.dataset.nnMobileThumbOrder &&
      selectedIndex > 0 && selectedIndex < visibleThumbnails.length) {
    visibleThumbnails.forEach((thumbnail, index) => {
      thumbnail.style.order = String((index - selectedIndex + visibleThumbnails.length) % visibleThumbnails.length);
    });
    this.dataset.nnMobileThumbOrder = String(selectedIndex);
    const container = this.querySelector('.slideshow-controls__thumbnails-container');
    requestAnimationFrame(() => { if (container) container.scrollLeft = 0; });
  }
  };

  #handleVisibleThumbnailClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const thumbnail = event.target.closest('.slideshow-controls__thumbnail');
    if (!thumbnail || thumbnail.hasAttribute('data-nn-color-hidden')) return;

    const visibleThumbnails = [...this.querySelectorAll('.slideshow-controls__thumbnail')]
      .filter((item) => !item.hasAttribute('data-nn-color-hidden'));
    const visibleIndex = visibleThumbnails.indexOf(thumbnail);
    if (visibleIndex < 0) return;

    // The theme's delegated click handler keeps the original all-media index.
    // Resolve the actual slide index from thumbnail identity so filtered
    // thumbnails and hidden media don't produce stale selection.
    event.preventDefault();
    event.stopImmediatePropagation();

    // The theme's document-level declarative handler runs before this gallery
    // capture listener and can still finish with the stale, all-media index.
    // Apply the resolved index on the next frame so it is the final selection.
    const targetIndex = this.#resolveVisibleMediaIndex(thumbnail, visibleIndex);
    if (targetIndex < 0) return;

    const slideshow = this.slideshow;
    requestAnimationFrame(() => {
      slideshow?.select(targetIndex, undefined, { animate: false });
    });
  };

  #resolveVisibleMediaIndex(thumbnail, fallbackIndex) {
    if (!(thumbnail instanceof Element) || fallbackIndex < 0) return fallbackIndex;

    const presetIndex = thumbnail.getAttribute('data-nn-slide-index');
    const resolvedPresetIndex = presetIndex === null ? Number.NaN : Number(presetIndex);
    if (Number.isInteger(resolvedPresetIndex) && resolvedPresetIndex >= 0) {
      return resolvedPresetIndex;
    }

    const { mediaId, mediaSrc } = this.#extractThumbnailMediaMeta(thumbnail);

    const slides = this.#getImageSlides(this.slideshow);
    if (!slides.length) return fallbackIndex;

    if (mediaId) {
      const byId = slides.findIndex((slide) => slide.getAttribute('data-media-id') === mediaId);
      if (byId >= 0) return byId;
    }

    if (mediaSrc) {
      const fileName = this.#normalizeMediaFileName(mediaSrc);
      const bySrc = slides.findIndex((slide) => {
        const image = slide.querySelector('img');
        const src = image?.currentSrc || image?.src || '';
        return this.#normalizeMediaFileName(src) === fileName;
      });
      if (bySrc >= 0) return bySrc;
    }

    return fallbackIndex;
  }

  #extractThumbnailMediaMeta(thumbnail) {
    const image = thumbnail.querySelector('img');
    const button = thumbnail.closest('button') || thumbnail;
    return {
      mediaId: thumbnail.getAttribute('data-media-id') || button.getAttribute('data-media-id') || '',
      mediaSrc:
        thumbnail.getAttribute('data-media-src') ||
        image?.dataset?.src ||
        image?.currentSrc ||
        image?.src ||
        '',
    };
  }

  #normalizeMediaFileName(url) {
    try {
      return String(url).split('?')[0].split('/').pop();
    } catch {
      return '';
    }
  }

  #getImageSlides(component) {
    return [...(component?.refs?.slides || [])].filter((slide) => {
      const image = slide.querySelector('img');
      return Boolean(image?.src || image?.currentSrc);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  #filterPsm04Media() {
    const colorFilteredProducts = new Set([
      '/products/nuzzlenest-thin-washable-dog-crate-pad',
      '/products/nuzzlenest-orthopedic-dog-crate-pad',
      '/products/nuzzlenest-deep-sleep-round-dog-bed',
      '/products/nuzzlenest-orthopedic-calming-sofa-bed',
      '/products/nuzzlenest-2in1-cat-cave-tent',
      '/products/nuzzlenest-luxury-donut-dog-bed',
      '/products/nuzzlenest-round-calming-cat-bed',
    ]);
    if (!colorFilteredProducts.has(location.pathname)) return;

    const apply = () => {
      const selectedColor = [...document.querySelectorAll('input[type="radio"]')]
        .find((input) => input.name.startsWith('Color-') && input.checked)?.value;
      if (!selectedColor) return;

      const imageSources = (image) => [
        image?.currentSrc,
        image?.src,
        image?.getAttribute?.('src'),
        image?.getAttribute?.('srcset'),
        image?.dataset?.src,
        image?.dataset?.srcset,
      ].filter(Boolean).join(' ');

      const keepImage = (image) => {
        const alt = image.alt || '';
        const src = imageSources(image);
        const isRejectedCpPowderBlueImage =
          location.pathname === '/products/nuzzlenest-orthopedic-dog-crate-pad' &&
          selectedColor === 'Powder Blue' &&
          src.toLowerCase().includes('05-powder-blue');
        if (isRejectedCpPowderBlueImage) return false;

        const colorSlug = selectedColor.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        const colorAliases = {
          gray: ['gray', 'grey'],
          grey: ['grey', 'gray'],
          cream: ['cream', 'white'],
        };
        const acceptedColorSlugs = colorAliases[colorSlug] || [colorSlug];
        const colorMatches = alt.includes(`- ${selectedColor}`)
          || acceptedColorSlugs.some((slug) => src.toLowerCase().includes(slug))
          || src.includes(`${location.pathname.split('/').pop()}-size-guide`);

        if (!colorMatches) return false;
if(location.pathname==='/products/nuzzlenest-round-calming-cat-bed' && colorSlug==='grey')return ["nn-cat-grey-complete-v1-main-01","nn-cat-grey-complete-v1-main-02","nn-cat-grey-complete-v1-main-03","nn-cat-grey-complete-v1-main-04","nn-cat-grey-complete-v1-main-05","nn-cat-grey-complete-v1-main-06","nn-cat-grey-complete-v1-guide"].some(n=>src.includes(n));
if(location.pathname==='/products/nuzzlenest-round-calming-cat-bed' && colorSlug==='wind-chime')return ["nn-cat-wind-chime-complete-v1-main-01","nn-cat-wind-chime-complete-v1-main-02","nn-cat-wind-chime-complete-v1-main-03","nn-cat-wind-chime-complete-v1-main-04","nn-cat-wind-chime-complete-v1-main-05","nn-cat-wind-chime-complete-v1-main-06","nn-cat-wind-chime-complete-v1-guide"].some(n=>src.includes(n));
if(location.pathname==='/products/nuzzlenest-round-calming-cat-bed' && colorSlug==='pink')return ["nn-cat-pink-complete-v1-main-01","nn-cat-pink-complete-v1-main-02","nn-cat-pink-complete-v1-main-03","nn-cat-pink-complete-v1-main-04","nn-cat-pink-complete-v1-main-05","nn-cat-pink-complete-v1-main-06","nn-cat-pink-complete-v1-guide"].some(n=>src.includes(n));
if(location.pathname==='/products/nuzzlenest-round-calming-cat-bed' && colorSlug==='cream')return ["nn-cat-cream-complete-v1-main-01","nn-cat-cream-complete-v1-main-02","nn-cat-cream-complete-v1-main-03","nn-cat-cream-complete-v1-main-04","nn-cat-cream-complete-v1-main-05","nn-cat-cream-complete-v1-main-06","nn-cat-cream-complete-v1-guide"].some(n=>src.includes(n));

        // Final colour-specific batches are uploaded with a stable sequence
        // suffix (01–07). Shopify can retain an older upload of the same file
        // and append a UUID to its CDN filename. When a final batch exists for
        // the selected colour, show exactly one newest asset for each sequence
        // number and hide all legacy colour images. This prevents duplicate
        // thumbnails without deleting recoverable media in Shopify admin.
        if(location.pathname==="/products/nuzzlenest-orthopedic-calming-sofa-bed")return src.includes("nuzzlenest-orthopedic-calming-sofa-bed-size-guide")||acceptedColorSlugs.some(slug=>(false || src.includes(`ar-${slug}-extra-`))||["30-beagle","36-border-collie","42-golden"].some(scene=>src.includes(`ar-${slug}-${scene}-v1-web`)));if (finalBatchBySequence.size > 0) {
          const sequence = getFinalSequence(src);
          if (!sequence) return false;
          return getVersion(src) === finalBatchBySequence.get(sequence);
        }

        return true;
      };

      const getFinalSequence = (src) => {
        const match = src.toLowerCase().match(/(?:final-v\d+|wind-chime-v\d+)-(\d{2})(?:_|\.|\?)/);
        return match?.[1] || '';
      };
      const getVersion = (src) => Number(new URL(src, location.href).searchParams.get('v') || 0);
      const finalBatchBySequence = new Map();

      [...this.querySelectorAll('img')].forEach((image) => {
        const src = image.currentSrc || image.src || '';
        const sequence = getFinalSequence(src);
        if (!sequence) return;

        const colorSlug = selectedColor.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        const colorAliases = { gray: ['gray', 'grey'], grey: ['grey', 'gray'], cream: ['cream', 'white'] };
        const acceptedColorSlugs = colorAliases[colorSlug] || [colorSlug];
        if (!acceptedColorSlugs.some((slug) => src.toLowerCase().includes(slug))) return;

        finalBatchBySequence.set(sequence, Math.max(finalBatchBySequence.get(sequence) || 0, getVersion(src)));
      });

      const mediaItems = this.querySelectorAll(
        'slideshow-slide, .slideshow-controls__thumbnail, .media-gallery__grid > li, .dialog-thumbnails-list button'
      );
      mediaItems.forEach((item) => {
        const image = item.querySelector('img');
        if (!image) return;
        const visible = keepImage(image);
        item.hidden = !visible;
        item.toggleAttribute('data-nn-color-hidden', !visible);
      });

      // The theme's slideshow indexes only visible slides, while the Liquid
      // thumbnail handlers are rendered with indexes from the complete product
      // media array. Re-index the controls after filtering so every thumbnail
      // selects the matching image for the currently selected color.
      const imageSlides = this.#getImageSlides(this.slideshow);
      const visibleThumbnails = [...this.querySelectorAll('.slideshow-controls__thumbnail')]
        .filter((thumbnail) => !thumbnail.hasAttribute('data-nn-color-hidden'));
      visibleThumbnails.forEach((thumbnail, index) => {
        const { mediaSrc } = this.#extractThumbnailMediaMeta(thumbnail);
        const fileName = this.#normalizeMediaFileName(mediaSrc);
        const slideIndex = imageSlides.findIndex((slide) => {
          const image = slide.querySelector('img');
          const src = image?.currentSrc || image?.src || '';
          return this.#normalizeMediaFileName(src) === fileName;
        });
        if (slideIndex >= 0) {
          thumbnail.setAttribute('data-nn-slide-index', String(slideIndex));
        }
        thumbnail.setAttribute('aria-selected', String(index === 0));
      });

      this.slideshow?.select(0, undefined, { animate: false });
    };

    requestAnimationFrame(apply);
    setTimeout(apply, 150);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
